import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';
import BtButton from '../bt-button/bt-button';
import { RequestStatus, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationLimits, EvaluationRecipientStatus } from '../../util/evaluationContract';
import { ManageHandlers, notifySend, useManageAction } from './manageActions';
import {
  AddRecipientsModal,
  CancelRequestModal,
  CloseRequestModal,
  EditTagsModal,
  ExtendDueDateModal,
  liveRecipientsOf,
  ReopenModal,
} from './RequestModals';
import styles from './page-supplier-evaluations.module.scss';

type Props = ManageHandlers & {
  status: RequestStatus;
  onOpenProfile: () => void;
};

type OpenModal = 'close' | 'dueDate' | 'cancel' | 'add' | 'tags' | 'reopen' | null;

const PENDING: string[] = [
  EvaluationRecipientStatus.Sent,
  EvaluationRecipientStatus.Opened,
  EvaluationRecipientStatus.Overdue,
  EvaluationRecipientStatus.Failed,
  EvaluationRecipientStatus.NotSent,
];

const RequestActions: FC<Props> = ({ status, onOpenProfile, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const [open, setOpen] = useState<OpenModal>(null);
  const reminding = useManageAction(handlers);
  const { canManage, canEditTags, canCancel, canReopen } = status;
  const hasPending = status.recipients.some(
    recipient => !recipient.isSelf && PENDING.includes(recipient.status),
  );
  const hasRoom = liveRecipientsOf(status).length < EvaluationLimits.MaxRecipientsPerRequest;

  const remind = () =>
    reminding.run(
      () => serviceEvaluations.remindPending(status.request._id),
      ({ outcome }) => {
        if (!outcome) return;
        notifySend(
          t,
          outcome,
          t('status.actions.reminded', {
            count: outcome.sent,
            defaultValue_one: 'Påminnelse skickad till {{count}} mottagare',
            defaultValue_other: 'Påminnelse skickad till {{count}} mottagare',
          }),
        );
      },
    );

  const menu = [
    ...(canManage
      ? [
          {
            label: t('status.actions.changeDueDate', 'Ändra sista svarsdag'),
            action: () => setOpen('dueDate'),
          },
          ...(hasRoom
            ? [
                {
                  label: t('status.actions.addRecipients', 'Lägg till mottagare'),
                  action: () => setOpen('add'),
                },
              ]
            : []),
        ]
      : []),
    ...(canEditTags
      ? [
          {
            label: t('status.actions.editTags', 'Ändra taggar'),
            action: () => setOpen('tags'),
          },
        ]
      : []),
    ...(canCancel
      ? [
          {
            label: t('status.actions.cancel', 'Avbryt utvärdering'),
            action: () => setOpen('cancel'),
          },
        ]
      : []),
  ];

  const modalProps = { ...handlers, status, onClose: () => setOpen(null) };

  return (
    <div className={styles.headerActions}>
      <BtButton color="white" tabIndex={0} onClick={onOpenProfile}>
        {t('status.viewEvaluations', 'Visa utvärderingar')}
      </BtButton>
      {canManage && (
        <BtButton color="white" tabIndex={0} onClick={() => setOpen('close')}>
          {t('status.actions.close', 'Stäng utvärdering')}
        </BtButton>
      )}
      {canManage && hasPending && (
        <BtButton tabIndex={0} onClick={remind} loaderShow={reminding.saving}>
          {t('status.actions.remindPending', 'Påminn väntande')}
        </BtButton>
      )}
      {canReopen && (
        <BtButton tabIndex={0} onClick={() => setOpen('reopen')}>
          {t('status.actions.reopen', 'Öppna igen')}
        </BtButton>
      )}
      <OptionsMenu options={menu} />
      {open === 'close' && <CloseRequestModal {...modalProps} />}
      {open === 'dueDate' && <ExtendDueDateModal {...modalProps} />}
      {open === 'cancel' && <CancelRequestModal {...modalProps} />}
      {open === 'add' && <AddRecipientsModal {...modalProps} />}
      {open === 'tags' && <EditTagsModal {...modalProps} />}
      {open === 'reopen' && <ReopenModal {...modalProps} />}
    </div>
  );
};

export default RequestActions;
